import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@jsverse/transloco';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter } from 'src/app/core/models/base';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { ExpenseService } from '../services/expense.service';
import {
  EMPTY_EXPENSE_GRID_RESULT,
  EXPENSES_SEARCH_PERMISSION,
  ExpenseStatus,
  IExpenseGridResult,
  ICreateExpenseRequest,
  IRejectExpenseRequest,
  ISearchExpensesRequest,
  IUpdateExpenseRequest,
  expenseSortToOrderBy,
  toExpenseGridResult
} from '../models/expense';

// Paging parameters passed from the list components on page/page-size change.
interface IPageRequest {
  pageNumber: number;
  pageSize: number;
}

// Filter criteria for the "Meine Spesen" list (status + expense-date range).
export interface IOwnExpensesFilter {
  statuses: ExpenseStatus[];
  advancedFilter?: IFilter;
}

// The full status set shown when no status filter is applied.
const DEFAULT_OWN_STATUSES = [
  ExpenseStatus.Open,
  ExpenseStatus.Submitted,
  ExpenseStatus.Accepted,
  ExpenseStatus.Rejected,
  ExpenseStatus.Paid
];

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ExpenseStoreService {

  // Tab 1 "Meine Spesen": own, all statuses. Without an explicit orderBy (the grid is unsorted) the
  // backend applies the status-grouped ordering (Open, Submitted, Accepted/Rejected), each by expense
  // date desc, which holds across the server-side pages. A column sort replaces that grouping.
  private _ownExpenses$ = new BehaviorSubject<IExpenseGridResult>(EMPTY_EXPENSE_GRID_RESULT);
  public ownExpenses$ = this._ownExpenses$.asObservable();

  // Server-side paging state for the own list; reused so mutations reload the current page.
  private ownRequest: ISearchExpensesRequest = {
    pageNumber: 1,
    pageSize: 25,
    statuses: [...DEFAULT_OWN_STATUSES]
  };

  // The request behind the currently displayed own list (filter, sort, page). The export widens
  // it to the whole filtered set, so it follows exactly what the list shows.
  get currentOwnRequest(): ISearchExpensesRequest {
    return { ...this.ownRequest };
  }

  // Tab 2 "Zu prüfen": subordinates, Submitted
  private _toReviewExpenses$ = new BehaviorSubject<IExpenseGridResult>(EMPTY_EXPENSE_GRID_RESULT);
  public toReviewExpenses$ = this._toReviewExpenses$.asObservable();

  // Server-side paging state for the review list; reused so decisions reload the current page.
  private toReviewRequest: ISearchExpensesRequest = {
    pageNumber: 1,
    pageSize: 25,
    orderBy: ['date desc'],
    forApproval: true,
    statuses: [ExpenseStatus.Submitted]
  };

  // Number of expenses the current user still has to review (drives the nav/tab badge).
  private _toReviewCount$ = new BehaviorSubject<number>(0);
  public toReviewCount$ = this._toReviewCount$.asObservable();

  constructor(
    private expenseService: ExpenseService,
    private notificationService: AppNotificationService,
    private translocoService: TranslocoService,
    private permissionStorage: PermissionStorageService
  ) { }

  private hasPermission(permission: string): boolean {
    return (this.permissionStorage.getPermissions() ?? []).includes(permission);
  }

  // sort: the grid's current sort. It is kept in the request so the reloads after create/edit/
  // submit/delete keep the order; an empty sort removes the orderBy again (backend grouping). The
  // list passes it on init too, because this store is root-provided and would otherwise keep a
  // sort from a previous visit while the grid shows none.
  loadOwn(page?: IPageRequest, filter?: IOwnExpensesFilter, sort?: SortDescriptor[]) {
    if (filter) {
      // Empty status selection means "all statuses" (the default set).
      this.ownRequest.statuses = filter.statuses.length ? filter.statuses : DEFAULT_OWN_STATUSES;
      this.ownRequest.advancedFilter = filter.advancedFilter;
    }
    if (page) {
      this.ownRequest.pageNumber = page.pageNumber;
      this.ownRequest.pageSize = page.pageSize;
    }
    if (sort) {
      this.ownRequest.orderBy = sort.length ? expenseSortToOrderBy(sort) : undefined;
    }
    this.expenseService.searchMyExpenses(this.ownRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => this._ownExpenses$.next(toExpenseGridResult(response)));
  }

  // orderBy: the grid's current sort (see expenseSortToOrderBy). It is kept in the request so the
  // reloads after a decision keep the order; the list passes it on init too, because this store is
  // root-provided and would otherwise outlive the component's default sort indicator.
  loadToReview(page?: IPageRequest, orderBy?: string[]) {
    // Without the expense module (or the expense role) the backend answers 403; never ask then.
    if (!this.hasPermission(EXPENSES_SEARCH_PERMISSION)) {
      return;
    }
    // Subordinates' submitted expenses — a regular supervisor never sees their own (subordinates
    // never include self); a holder of Expenses.SearchAll (e.g. HR) sees every employee's.
    if (page) {
      this.toReviewRequest.pageNumber = page.pageNumber;
      this.toReviewRequest.pageSize = page.pageSize;
    }
    if (orderBy) {
      this.toReviewRequest.orderBy = orderBy;
    }
    this.expenseService.searchExpensesForApproval(this.toReviewRequest)
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this._toReviewExpenses$.next(toExpenseGridResult(response));
        this._toReviewCount$.next(response.totalCount);
      });
  }

  // Lightweight count-only load for the nav/tab badge (used e.g. on app startup, without
  // fetching the whole list).
  loadToReviewCount() {
    // Called unconditionally on app start (AppComponent); on a tenant without the expense module
    // the permission is missing and the request would come back as 403.
    if (!this.hasPermission(EXPENSES_SEARCH_PERMISSION)) {
      return;
    }
    this.expenseService.searchExpensesForApproval({
      pageNumber: 1,
      pageSize: 1,
      forApproval: true,
      statuses: [ExpenseStatus.Submitted]
    })
      .pipe(untilDestroyed(this))
      .subscribe(response => this._toReviewCount$.next(response.totalCount));
  }

  private reloadOwn() {
    this.loadOwn();
  }

  createExpense(request: ICreateExpenseRequest, onDone?: (id: string) => void) {
    this.expenseService.createExpense(request)
      .pipe(untilDestroyed(this))
      .subscribe(id => {
        this.reloadOwn();
        this.notify('expenses.createdSuccess');
        onDone?.(id);
      });
  }

  updateExpense(request: IUpdateExpenseRequest, onDone?: () => void) {
    this.expenseService.updateExpense(request)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.reloadOwn();
        this.notify('expenses.updatedSuccess');
        onDone?.();
      });
  }

  submitExpense(id: string, onDone?: () => void) {
    this.expenseService.submitExpense(id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.reloadOwn();
        this.notify('expenses.submittedSuccess');
        onDone?.();
      });
  }

  withdrawExpense(id: string) {
    this.expenseService.withdrawExpense(id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.reloadOwn();
          this.notify('expenses.withdrawnSuccess');
        },
        // On a racing decision the request fails (the error interceptor shows the conflict message);
        // reload so the list reflects the expense's real current status (e.g. now Accepted/Rejected).
        error: () => this.reloadOwn()
      });
  }

  deleteExpense(id: string) {
    this.expenseService.deleteExpense(id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.reloadOwn();
        this.notify('expenses.deletedSuccess');
      });
  }

  approveExpense(id: string, onDone?: () => void) {
    this.expenseService.approveExpense(id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadToReview();
        this.notify('expenses.approvedSuccess');
        onDone?.();
      });
  }

  approveMany(ids: string[]) {
    if (!ids.length) {
      return;
    }
    forkJoin(ids.map(id => this.expenseService.approveExpense(id)))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadToReview();
        this.notify('expenses.approvedSuccess');
      });
  }

  rejectExpense(request: IRejectExpenseRequest, onDone?: () => void) {
    this.expenseService.rejectExpense(request)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadToReview();
        this.notify('expenses.rejectedSuccess');
        onDone?.();
      });
  }

  private notify(translationKey: string) {
    this.notificationService.showNotification({
      message: this.translocoService.translate(translationKey),
      style: 'success'
    });
  }
}
